import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  BarChart3, Brain, Cloud, Headset, KeyRound, Laptop, Radar, Rocket,
  ShieldCheck, Smartphone, Workflow, Zap,
} from 'lucide-react';
import { IMG, STATS } from '../data';
import { EASE, Reveal, SectionHead, useApp } from './shared';

const THEME_ICONS = {
  'Multi / Hybrid Clouds': Cloud,
  'Digital Transformation': Rocket,
  'Enterprise Security': ShieldCheck,
  'AI / ML': Brain,
  'Data Analytics': BarChart3,
  'CX / ITSM': Headset,
  'Remote Working': Laptop,
  'Hyperautomation': Zap,
  'Zero Trust Security': KeyRound,
  'Digital Workflow': Workflow,
  'Mobile Technology': Smartphone,
  'Internet of Things (IoT)': Radar,
};

const THEMES_12 = Object.keys(THEME_ICONS);

function Counter({ to, suffix, padZero = false }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1700;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);

  const displayN = padZero && n < 10 ? `0${n}` : n;

  return (
    <span ref={ref} className="font-display text-5xl font-extrabold tracking-tight sm:text-6xl" data-testid="stat-counter">
      {displayN}
      <span className="text-lime">{suffix}</span>
    </span>
  );
}

export default function Overview() {
  const { openModal } = useApp();

  return (
    <>
      <section id="overview" className="tex-dots relative py-24 md:py-32">
        <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-5 md:px-8 lg:grid-cols-2 lg:gap-20">
          <div>
            <SectionHead
              eyebrow="An Overview"
              title={
                <>
                  The CXO<span className="text-lime">100</span> 2027
                </>
              }
            />
            <Reveal delay={0.1}>
              <p className="border-l-2 border-lime/60 pl-5 font-mono text-xs uppercase leading-loose tracking-[0.2em] text-lime">
                Unleashing the Power of AI &amp; Cybersecurity for Business Resiliency
              </p>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-8 leading-relaxed text-slate-300">
                The convergence of Artificial Intelligence (AI) and Cybersecurity presents both unprecedented
                opportunities and significant challenges for businesses. The CXO100 2027 is designed for C-suite
                leaders who want to master this new landscape. We&apos;ll explore how to harness the power of AI to
                drive innovation and efficiency while simultaneously building a robust and resilient security posture.
              </p>
            </Reveal>
            <Reveal delay={0.22}>
              <p className="mt-5 leading-relaxed text-slate-300">
                You&apos;ll learn to leverage AI for proactive threat detection, automate defenses, and turn security
                from a cost center into a strategic enabler. Join us to discover how to navigate these two critical
                domains and create a future-proof enterprise.
              </p>
            </Reveal>
            <Reveal delay={0.28}>
              <p className="mt-5 leading-relaxed text-slate-300">
                Presented by <span className="font-semibold text-slate-100">CIO AXIS</span>, the summit will feature
                insightful keynotes, engaging panel discussions, focused CXO roundtables, AI-workshops, and valuable
                networking opportunities — celebrating the achievements of top{' '}
                <span className="text-lime">CIOs, CISOs, CTOs, CDOs, AI Leaders, Digital Transformation champions</span>.
                Join us in New Delhi (Feb 05, 2027) and Mumbai (Feb 19, 2027), for an unforgettable experience.
              </p>
            </Reveal>
            <Reveal delay={0.34}>
              <button
                data-testid="overview-interest-button"
                onClick={() => openModal('register')}
                className="mt-8 inline-flex items-center gap-3 rounded-full bg-lime px-7 py-3.5 font-display text-xs font-bold uppercase tracking-widest text-ink transition-all duration-300 hover:shadow-[0_0_36px_rgba(153,204,0,0.45)]"
              >
                Express Interest
              </button>

            </Reveal>
          </div>

          <Reveal delay={0.15} className="relative">
            <div className="clip-frame relative overflow-hidden" data-testid="overview-image">
              <motion.img
                src={IMG('overview.jpg')}
                alt="The CXO100 Summit"
                className="h-[420px] w-full object-cover lg:h-[540px]"
                initial={{ scale: 1.15 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 1.6, ease: EASE }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
            </div>
            <div className="glass absolute -bottom-6 left-6 rounded-xl border border-lime/30 px-6 py-4 shadow-2xl md:left-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">Save the dates</p>
              <p className="mt-1 font-display text-base font-bold text-lime">FEB 05, 2027 · New Delhi</p>
              <p className="mt-0.5 font-display text-base font-bold text-lime">FEB 19, 2027 · Mumbai</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="stats" className="border-y border-line/10 bg-gradient-to-r from-ink-2 via-ink-3/80 to-ink-2 py-16 md:py-20">
        <div className="mx-auto grid w-full max-w-7xl grid-cols-2 gap-y-12 px-5 md:px-8 sm:grid-cols-3 lg:grid-cols-5">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="text-center">
              <div data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <Counter to={s.n} suffix={s.suffix} padZero={s.padZero} />
                <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.3em] text-slate-400">{s.label}</p>
                {s.note && (
                  <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">({s.note})</p>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="topics" className="tex-lines py-24 md:py-32">
        <div className="mx-auto w-full max-w-7xl px-5 md:px-8">
          <SectionHead
            eyebrow="Themes"
            title={
              <>
                Key <span className="text-lime">Themes</span>
              </>
            }
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {THEMES_12.map((t, i) => {
              const Icon = THEME_ICONS[t];
              return (
                <Reveal key={t} delay={(i % 4) * 0.06}>
                  <div
                    data-testid={`topic-card-${i}`}
                    className="group h-full rounded-xl border border-line/10 bg-ink-2/50 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-lime/50 hover:bg-lime/5 hover:shadow-[0_8px_40px_rgba(153,204,0,0.12)]"
                  >
                    <Icon
                      size={26}
                      strokeWidth={1.6}
                      className="text-lime transition-transform duration-300 group-hover:scale-110"
                    />
                    <p className="mt-5 font-display text-sm font-bold leading-snug tracking-tight sm:text-base">
                      {t}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
